<script lang="ts">
    import { modalManagerToken } from "@/tokens";
    import LoadMoreButton from "@apps/iloom_customization/components/LoadMoreButton.svelte";
    import Page from "@apps/iloom_customization/components/Page.svelte";
    import PageTitle from "@apps/iloom_customization/components/PageTitle.svelte";
    import Sort from "@apps/iloom_customization/components/Sort.svelte";
    import { SPACE_TAB_LIST } from "@apps/iloom_customization/constants/constants";
    import DesignFeedMasonry from "@apps/iloom_customization/design_feed/components/DesignFeedMasonry.svelte";
    import DesignFeedModal from "@apps/iloom_customization/design_feed/components/DesignFeedModal/DesignFeedModal.svelte";
    import DesignFeedTabs from "@apps/iloom_customization/design_feed/components/DesignFeedTabs.svelte";
    import { MediaListQuery } from "@apps/iloom_customization/design_feed/queries/MediaListQuery";
    import { DesignFeedService } from "@apps/iloom_customization/design_feed/services/DesignFeedService";
    import { ProductQuery } from "@apps/iloom_customization/modules/products/queries/ProductQuery";
    import { ProductService } from "@apps/iloom_customization/modules/products/services/ProductService";
    import { WishlistService } from "@apps/iloom_customization/wishlist/services/WishlistService";
    import { WishlistProductStatusesQuery } from "@apps/wishlist/queries/WishlistProductStatusesQuery";
    import { WishlistsService } from "@apps/wishlist/services/WishlistsService";
    import { CartQuery } from "@modules/cart/queries/CartQuery";
    import { CartService } from "@modules/cart/services/CartService";
    import { BehaviorSubject, combineLatest, lastValueFrom } from "rxjs";
    import { onMount } from "svelte";
    import { container } from "tsyringe";

    import Portal from "@components/Portal/Portal.svelte";
    import TabsManager from "@components/Tabs/TabsManager";

    export let cartQuery: CartQuery;
    export let cartService: CartService;
    export let designFeedService: DesignFeedService;
    export let mediaListQuery: MediaListQuery;
    export let productQuery: ProductQuery;
    export let productService: ProductService;
    export let spaceTabsManager: TabsManager;
    export let wishlistService: WishlistService;
    export let wishlistsService: WishlistsService;
    export let wishlistProductStatusesQuery: WishlistProductStatusesQuery;

    const modalManager = container.resolve(modalManagerToken);

    const openedSpaceTab$ = spaceTabsManager.openedTab$;

    const meta$ = mediaListQuery.meta$;
    const loadMoreStatus$ = mediaListQuery.loadMoreStatus$;

    const sortTabs = [
        {
            value: "new",
            name: "最新的",
        },
        {
            value: "popular",
            name: "按受歡迎程度排序",
        },
    ];

    type SortType = "new" | "popular";

    const currentSort$ = new BehaviorSubject<SortType>("new");

    const handleChangeSort = (event: CustomEvent) =>
        currentSort$.next(event.detail);

    const getMediaList = (offset = 0) => {
        if ($openedSpaceTab$) {
            const params = {
                offset,
                limit: $meta$.limit,
                collections:
                    $openedSpaceTab$ === "all" ? undefined : [$openedSpaceTab$],
                sort: $currentSort$,
            };
            lastValueFrom(designFeedService.getMediaList(params));
        }
    };

    onMount(() => {
        spaceTabsManager.openTab("all");

        const changeTabSubscription = combineLatest({
            openedSpaceTab$,
            currentSort$,
        }).subscribe(() => {
            getMediaList();
        });

        return () => {
            changeTabSubscription.unsubscribe();
        };
    });
</script>

<Page>
    <PageTitle class="gw-df-page-title">設計摘要</PageTitle>
    <DesignFeedTabs tabs={SPACE_TAB_LIST} tabsManager={spaceTabsManager} />
    <Sort
        tabs={sortTabs}
        on:sort={handleChangeSort}
        currentSort={$currentSort$}
    />
    <DesignFeedMasonry {designFeedService} {mediaListQuery} {modalManager} />
    <LoadMoreButton
        loadMore={getMediaList}
        loadMoreStatus={$loadMoreStatus$}
        meta={$meta$}
    />
</Page>

<Portal
    Component={DesignFeedModal}
    componentProps={{
        cartQuery,
        cartService,
        designFeedService,
        mediaListQuery,
        modalManager,
        productQuery,
        productService,
        wishlistService,
        wishlistsService,
        wishlistProductStatusesQuery,
    }}
    selector="body"
/>

<style lang="postcss">
    :global(.gw-page-title.gw-df-page-title) {
        margin-bottom: 60px;
        @media (max-width: 600px) {
            margin-bottom: 26px;
            font-size: 18px;
        }
    }
</style>
